import {
  DashboardHoldings,
  DashboardInvestmentTransactions,
  TransactionAllotmentConfirmation,
  TransactionPaymentCopy,
  TransactionTermsheet,
  UserInvestmentDetails,
  Withdrawal,
} from '../../lib/types/propsType_v1'
import { withdrawalServices } from '../../services'
import { WithdrawalSearchParams } from '../../services/types'
import { normalizeTransactionQuantity, resolveTransactionAmountCents } from './dashboard-v2.utils'

type HoldingTransactionRaw = {
  opportunity_name?: string
  date?: string
  created_at?: string
  txn_type?: string
  amount?: number | string
  quantity?: number | string | null
  unit_price?: number | string | null
  order_status?: string
  portfolio_id?: string
  order_id?: string
  termsheet?: TransactionTermsheet | null
  allotmentConfirmation?: TransactionAllotmentConfirmation | null
  paymentCopy?: TransactionPaymentCopy | null
  actual_amount_received?: number | null
  actual_invested_amount?: number | null
  actual_amount_sent?: number | null
  shares?: number | null
  price_per_share?: number | null
}

export const getInvestorIdentity = (name?: string, email?: string) => {
  const investorDisplayName = name || email || 'Client'
  const investorInitial = investorDisplayName.trim().charAt(0).toUpperCase()
  return { investorDisplayName, investorInitial }
}

export const getInvestmentLimitSummary = (
  userInvestmentDetail: UserInvestmentDetails | undefined,
  holdingsData: DashboardHoldings[],
) => {
  // Used = total invested principal. Wallet balance, top-ups, interest and dividends
  // never count towards it.
  const totalInvestment = Math.max(0, Number(userInvestmentDetail?.totalInvestment || 0))
  const investmentLimit = Number(userInvestmentDetail?.investmentLimit || 0)
  const remaining = Math.max(0, investmentLimit - totalInvestment)
  const usedPct = investmentLimit > 0 ? Math.min(100, (totalInvestment / investmentLimit) * 100) : 0
  // Active holdings = number of distinct opportunities the investor still holds units in.
  const activeHoldings = new Set(
    holdingsData
      .filter((holding) => Number(holding.total_units) > 0)
      .map((holding) => holding.opportunity_id),
  ).size

  return {
    totalInvestment,
    investmentLimit,
    usedPct,
    remaining,
    activeHoldings,
  }
}

export const buildPerfSeriesDollars = (perfSeriesCents: Array<{ date: string; value: number }>) => {
  const raw = perfSeriesCents.map((item) => ({ ...item, value: item.value / 100 }))
  if (raw.length === 1) {
    const only = raw[0]
    const d = new Date(only.date)
    d.setDate(d.getDate() - 1)
    return [{ ...only, date: d.toISOString().slice(0, 10) }, only]
  }
  return raw
}

export const buildHoldingsSnapshotData = (holdingsData: DashboardHoldings[]) =>
  holdingsData.slice(0, 8).map((h) => ({
    name: h.opportunity_title,
    invested: (h.invested_amount || 0) / 100,
    current: (h.current_price || 0) / 100,
  }))

const toNullableNumber = (value: number | string | null | undefined): number | null => {
  if (value === null || value === undefined) return null
  const n = Number(value)
  return Number.isFinite(n) ? n : null
}

export const mapHoldingTransactionHistory = (
  holdings: DashboardHoldings[],
  transactionHistory: HoldingTransactionRaw[][],
): DashboardInvestmentTransactions[] => {
  const mappedTransactions = transactionHistory.flatMap((group, idx: number) => {
    const holding = holdings[idx]
    return (group || []).map((t) => ({
      opportunity_title: t.opportunity_name || holding?.opportunity_title || '-',
      asset_class: holding?.asset_class || '-',
      transaction_date: t.date || t.created_at || '',
      transaction_type: t.txn_type || '-',
      amount: resolveTransactionAmountCents(t),
      quantity: normalizeTransactionQuantity(t.quantity),
      order_status: t.order_status || '-',
      portfolio_id: t.portfolio_id || '-',
      order_id: t.order_id || '-',
      termsheet: t.termsheet ?? null,
      allotmentConfirmation: t.allotmentConfirmation ?? null,
      paymentCopy: t.paymentCopy ?? null,
      actual_amount_received: toNullableNumber(t.actual_amount_received),
      actual_invested_amount: toNullableNumber(t.actual_invested_amount),
      actual_amount_sent: toNullableNumber(t.actual_amount_sent),
      shares: toNullableNumber(t.shares),
      price_per_share: toNullableNumber(t.price_per_share),
    })) as DashboardInvestmentTransactions[]
  })

  mappedTransactions.sort(
    (a, b) => new Date(b.transaction_date).getTime() - new Date(a.transaction_date).getTime(),
  )

  return mappedTransactions
}

export const fetchAllWithdrawalsForInvestor = async (
  userId: number,
  authToken: string,
  pageSize = 100,
): Promise<Withdrawal[]> => {
  const headers = { Authorization: authToken }
  const all: Withdrawal[] = []
  let offset = 0
  let total = Infinity

  while (offset < total) {
    const params: WithdrawalSearchParams = {
      investor_id: userId,
      limit: pageSize,
      offset,
    }
    const res = await withdrawalServices.searchWithdrawals(params, headers)
    if (res?.status !== 200 || !Array.isArray(res.withdrawals)) break

    all.push(...res.withdrawals)
    total = typeof res.total === 'number' ? res.total : all.length
    if (res.withdrawals.length < pageSize) break
    offset += pageSize
  }

  return all.sort((a, b) => {
    const da = a.metadata?.withdrawalInitiatedDate
    const db = b.metadata?.withdrawalInitiatedDate
    if (da && db) return new Date(db).getTime() - new Date(da).getTime()
    return b.id - a.id
  })
}
